import { Dropdown, Icon, Space, Spinner, Tag } from '@sravni/react-design-system';
import { ArrowDown, Calendar } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';
import { useEffect, useState } from 'react';

import { sendEventRatesChartChange, sendEventRatesChartInit } from 'shared/lib/sendGAEvents';

import { useGetLoanChartFiltersQuery } from 'entities/chartFilters';

import { useFiltersData } from '../../lib/hooks/useFiltersData';
import { useMonthDropdownData } from '../../lib/hooks/useMonthsDropdownData';

interface RatesControlsProps {
  setData: ({ type, sum, term, month }: { type: string; sum: string; term: string; month?: string }) => void;
  months: string[];
  changeMonth: (month: string) => void;
}

export const RatesControls = ({ setData, months, changeMonth }: RatesControlsProps) => {
  const isMobile = useIsMobile();
  const { data, isLoading } = useGetLoanChartFiltersQuery();
  const { types, sums, terms, selectedType, selectedSum, selectedTerm, recalculate } = useFiltersData(data || []);
  const { monthsOptions, curMonth, onMonthChange } = useMonthDropdownData(months, changeMonth);
  const [isInitAnalyticsSent, setInitAnalyticsSent] = useState(false);

  useEffect(() => {
    if (selectedType && selectedSum && selectedTerm && curMonth && !isInitAnalyticsSent) {
      sendEventRatesChartInit({
        type: selectedType.label,
        sum: selectedSum.label,
        term: selectedTerm.label,
        month: curMonth?.label,
      });

      setInitAnalyticsSent(true);
    }
  }, [selectedType, selectedSum, selectedTerm, curMonth, isInitAnalyticsSent]);

  useEffect(() => {
    if (selectedType && selectedSum && selectedTerm) {
      setData({ type: selectedType.value, sum: selectedSum.value, term: selectedTerm.value, month: curMonth?.value });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, selectedSum, selectedTerm]);

  if (isLoading) return <Spinner />;

  return (
    <Space
      size={12}
      justify={isMobile ? 'start' : 'space-around'}
      wrap={isMobile}
    >
      <Space
        size={12}
        wrap={isMobile}
        align="flex-start"
        direction={isMobile ? 'vertical' : 'horizontal'}
      >
        <Dropdown
          options={types}
          value={selectedType?.value || ''}
          onChange={(value) => {
            const newSelectedType = types.find((type) => type.value === value) || null;
            sendEventRatesChartChange('Тип кредита', newSelectedType?.label || '');
            recalculate(newSelectedType, null, null);
          }}
        >
          <Tag>
            {selectedType?.label} <Icon icon={<ArrowDown />} />
          </Tag>
        </Dropdown>
        <Space size={isMobile ? 8 : 12}>
          <Dropdown
            options={sums}
            value={selectedSum?.value || ''}
            onChange={(value) => {
              const newSelectedSum = sums.find((sum) => sum.value === value) || null;
              sendEventRatesChartChange('Сумма кредита', newSelectedSum?.label || '');
              recalculate(selectedType, newSelectedSum, null);
            }}
          >
            <Tag>
              {selectedSum?.label} <Icon icon={<ArrowDown />} />
            </Tag>
          </Dropdown>
          <Dropdown
            options={terms}
            value={selectedTerm?.value || ''}
            onChange={(value) => {
              const newSelectedTerm = terms.find((term) => term.value === value) || null;
              sendEventRatesChartChange('Срок кредита', newSelectedTerm?.label || '');
              recalculate(selectedType, selectedSum, newSelectedTerm);
            }}
          >
            <Tag>
              {selectedTerm?.label} <Icon icon={<ArrowDown />} />
            </Tag>
          </Dropdown>
        </Space>
      </Space>
      {monthsOptions.length && curMonth ? (
        <Dropdown
          options={monthsOptions}
          value={curMonth.value}
          onChange={(value) => {
            sendEventRatesChartChange('Период', value.toString() || '');
            return onMonthChange(value.toString());
          }}
        >
          <Tag>
            <Space
              size={4}
              align="center"
            >
              <Icon icon={<Calendar />} /> {curMonth?.label}
            </Space>
            <Icon icon={<ArrowDown />} />
          </Tag>
        </Dropdown>
      ) : (
        <Spinner />
      )}
    </Space>
  );
};
